<template>
  <SidebarNavigation :uri="uri">
    <div class="w-full mx-auto">
      <h2 class="flex justify-center font-bold text-xl sm:text-2xl mb-4 sm:mb-6 border-b-2 pb-3 sm:pb-4 px-4 sm:px-0">{{ __('VCF (vCard File) Generator') }}</h2>
            <div class="md:mt-10 sm:mt-3 justify-center flex flex-col lg:flex-row w-full mx-auto card-properties gap-4 lg:gap-6 px-4 sm:px-6 lg:px-0">
              <div class="card-form w-full lg:w-1/2 mx-auto">
                <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
                  <!-- Basic Information Section -->
                  <div class="mb-6 pb-6 border-b border-gray-200">
                    <h3 class="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                      <icon name="user" class="w-5 h-5 text-green-600" />
                      Basic Information
                    </h3>
                    <div class="space-y-4">
                      <!-- Photo Upload -->
                      <div class="flex w-full flex-col">
                        <label class="block mb-2 text-sm font-medium text-gray-700" for="vcardPhoto">
                          Profile Photo
                          <span class="text-xs text-gray-500 font-normal ml-1">(Optional, Recommended: 200x200px)</span>
                        </label>
                        <div class="relative">
                          <input
                            id="vcardPhoto"
                            class="hidden"
                            type="file"
                            @change="logoInputChange"
                            accept="image/*"
                          >
                          <label
                            for="vcardPhoto"
                            class="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 hover:border-green-400 transition-colors group"
                          >
                            <div v-if="form.logo" class="flex flex-col items-center justify-center pt-2 pb-2">
                              <img :src="form.logo" alt="Photo preview" class="h-16 w-16 object-contain mb-2 rounded" />
                              <p class="text-sm text-gray-600">Click to change photo</p>
                            </div>
                            <div v-else class="flex flex-col items-center justify-center pt-2 pb-2">
                              <icon name="camera" class="w-8 h-8 text-gray-400 group-hover:text-green-600 mb-2" />
                              <p class="text-sm text-gray-600 group-hover:text-green-600">
                                <span class="font-semibold">Click to upload</span> or drag and drop
                              </p>
                              <p class="text-xs text-gray-500 mt-1">PNG, JPG up to 5MB</p>
                            </div>
                          </label>
                        </div>
                      </div>
                      <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <text-input v-model="form.first_name" :error="form.errors.first_name" class="w-full pr-6" :label="__('First Name')" type="text" autofocus autocapitalize="off" maxlength="50" />
                        <text-input v-model="form.last_name" :error="form.errors.last_name" class="w-full pr-6" :label="__('Last Name')" type="text" autofocus autocapitalize="off" maxlength="50" />
                      </div>
                    </div>
                  </div>

                  <!-- Contact Information Section -->
                  <div class="mb-6 pb-6 border-b border-gray-200">
                    <div class="flex items-center justify-between mb-4">
                      <h3 class="text-lg font-semibold text-gray-800 flex items-center gap-2">
                        <icon name="phone" class="w-5 h-5 text-green-600" />
                        Phone Numbers
                      </h3>
                      <button
                        @click="addNewEntry('phone')"
                        class="text-sm text-green-600 hover:text-green-700 font-medium flex items-center gap-1"
                      >
                        <icon name="plus" class="w-4 h-4" />
                        Add Phone
                      </button>
                    </div>
                    <div class="space-y-3">
                      <div v-for="(phone, pi) in form.phones" :key="pi" class="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                        <select v-model="phone.type" class="bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 block w-28 px-3 py-2">
                          <option value="Work">Work</option>
                          <option value="Main">Main</option>
                          <option value="Home">Home</option>
                          <option value="Cell">Cell</option>
                          <option value="Fax">Fax</option>
                          <option value="Other">Other</option>
                        </select>
                        <div class="flex-1">
                            <input :id="'phone__'+pi" v-model="phone.phone" @blur="validatePhoneEntry(phone, pi)" class="form-input w-full" :class="{ 'border-red-500': validationErrors[`phones.${pi}`] }" type="tel" placeholder="+1 (555) 123-4567" maxlength="20" pattern="[\d\s\-\+\(\)\.]{10,}" />
                            <p v-if="validationErrors[`phones.${pi}`]" class="text-xs text-red-500 mt-1">{{ validationErrors[`phones.${pi}`] }}</p>
                        </div>
                        <button
                          v-if="pi > 0"
                          @click="removeEntry(pi, form.phones)"
                          class="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                          title="Remove phone"
                        >
                          <Icon name="trash" class="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                  <div class="mb-6 pb-6 border-b border-gray-200">
                    <div class="flex items-center justify-between mb-4">
                      <h3 class="text-lg font-semibold text-gray-800 flex items-center gap-2">
                        <icon name="email" class="w-5 h-5 text-green-600" />
                        Email Addresses
                      </h3>
                      <button
                        @click="addNewEntry('email')"
                        class="text-sm text-green-600 hover:text-green-700 font-medium flex items-center gap-1"
                      >
                        <icon name="plus" class="w-4 h-4" />
                        Add Email
                      </button>
                    </div>
                    <div class="space-y-3">
                      <div v-for="(email, ei) in form.emails" :key="ei" class="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                        <select v-model="email.type" class="bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 block w-28 px-3 py-2">
                          <option value="Work">Work</option>
                          <option value="Home">Home</option>
                          <option value="Other">Other</option>
                        </select>
                        <div class="flex-1">
                            <input :id="'email__'+ei" v-model="email.email" @blur="validateEmailEntry(email, ei)" class="form-input w-full" :class="{ 'border-red-500': validationErrors[`emails.${ei}`] }" type="email" placeholder="email@example.com" maxlength="255" />
                            <p v-if="validationErrors[`emails.${ei}`]" class="text-xs text-red-500 mt-1">{{ validationErrors[`emails.${ei}`] }}</p>
                        </div>
                        <button
                          v-if="ei > 0"
                          @click="removeEntry(ei, form.emails)"
                          class="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                          title="Remove email"
                        >
                          <Icon name="trash" class="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                  <div class="mb-6 pb-6 border-b border-gray-200">
                    <div class="flex items-center justify-between mb-4">
                      <h3 class="text-lg font-semibold text-gray-800 flex items-center gap-2">
                        <icon name="website" class="w-5 h-5 text-green-600" />
                        Websites
                      </h3>
                      <button
                        @click="addNewEntry('website')"
                        class="text-sm text-green-600 hover:text-green-700 font-medium flex items-center gap-1"
                      >
                        <icon name="plus" class="w-4 h-4" />
                        Add Website
                      </button>
                    </div>
                    <div class="space-y-3">
                      <div v-for="(website, wi) in form.websites" :key="wi" class="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                        <select v-model="website.type" class="bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 block w-28 px-3 py-2">
                          <option value="Work">Work</option>
                          <option value="Home">Home</option>
                          <option value="Other">Other</option>
                        </select>
                        <div class="flex-1">
                            <input :id="'website__'+wi" v-model="website.website" @blur="validateWebsiteEntry(website, wi)" class="form-input w-full" :class="{ 'border-red-500': validationErrors[`websites.${wi}`] }" type="url" placeholder="https://example.com" maxlength="255" />
                            <p v-if="validationErrors[`websites.${wi}`]" class="text-xs text-red-500 mt-1">{{ validationErrors[`websites.${wi}`] }}</p>
                        </div>
                        <button
                          v-if="wi > 0"
                          @click="removeEntry(wi, form.websites)"
                          class="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                          title="Remove website"
                        >
                          <Icon name="trash" class="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                  <div class="mb-6 pb-6 border-b border-gray-200">
                    <div class="flex items-center justify-between mb-4">
                      <h3 class="text-lg font-semibold text-gray-800 flex items-center gap-2">
                        <icon name="location" class="w-5 h-5 text-green-600" />
                        Addresses
                      </h3>
                      <button
                        @click="addNewEntry('address')"
                        class="text-sm text-green-600 hover:text-green-700 font-medium flex items-center gap-1"
                      >
                        <icon name="plus" class="w-4 h-4" />
                        Add Address
                      </button>
                    </div>
                    <div class="space-y-3">
                      <div v-for="(address, ai) in form.addresses" :key="ai" class="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                        <div class="flex items-start gap-3">
                          <select v-model="address.type" class="bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 block w-28 px-3 py-2">
                            <option value="Work">Work</option>
                            <option value="Home">Home</option>
                            <option value="Mailing">Mailing</option>
                            <option value="Other">Other</option>
                          </select>
                          <div class="flex flex-col w-full gap-2">
                            <input :id="'a_name_'+ai" v-model="address.name" class="form-input" type="text" placeholder="Address name/label" maxlength="50" />
                            <input :id="'a_address_'+ai" v-model="address.address" class="form-input w-full" type="text" placeholder="Street address, City, State, ZIP" maxlength="200" />
                          </div>
                          <button
                            v-if="ai > 0"
                            @click="removeEntry(ai, form.addresses)"
                            class="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                            title="Remove address"
                          >
                            <Icon name="trash" class="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Generate & Share Section -->
                <div class="mt-6 pt-6 border-t border-gray-200">
                  <h3 class="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <icon name="send" class="w-5 h-5 text-green-600" />
                    Generate & Share
                  </h3>
                  <div class="space-y-4">
                    <button
                      @click="generateVCF()"
                      class="w-full text-white flex justify-center items-center bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 active:from-green-800 active:to-green-900 focus:outline-none rounded-lg px-6 py-3 gap-2 font-semibold transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                    >
                      <icon name="send" class="w-5 h-5 text-white fill-white" />
                      Generate vCard Link
                    </button>

                    <div v-if="form.card_link" class="space-y-2">
                      <label class="block text-sm font-medium text-gray-700">vCard File URL</label>
                      <div class="flex gap-2">
                        <input
                          v-model="form.card_link"
                          type="text"
                          class="flex-1 px-4 py-2 bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 cursor-text"
                          readonly
                        />
                        <button
                          @click="copyURL"
                          :disabled="copy_text"
                          :class="[
                            'px-4 py-2 rounded-lg text-white font-medium transition-all duration-200 shadow-md hover:shadow-lg',
                            copy_text
                              ? 'bg-green-600 cursor-default'
                              : 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800'
                          ]"
                        >
                          <span v-if="copy_text" class="flex items-center gap-1">
                            <icon name="tick" class="w-4 h-4" />
                            Copied!
                          </span>
                          <span v-else class="flex items-center gap-1">
                            <icon name="file" class="w-4 h-4" />
                            Copy
                          </span>
                        </button>
                      </div>
                      <p class="text-xs text-gray-500">
                        Share this link to let others download your contact information as a vCard file
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div class="card-items flex w-full lg:w-1/2 mx-auto py-0">
                <div class="flex flex-wrap flex-col items-center w-full bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
                  <!-- Preview Header -->
                  <div class="w-full mb-4 pb-4 border-b border-gray-200">
                    <h3 class="text-lg font-semibold text-gray-800 flex items-center gap-2 mb-2">
                      <icon name="aperture" class="w-5 h-5 text-green-600" />
                      Live Preview
                    </h3>
                    <p class="text-xs text-gray-500">See how your vCard will appear on mobile devices</p>
                  </div>
                  <div class="phone_layout w-full px-4 sm:px-6 lg:px-8">
                    <div class="live__preview justify-center items-center bg-[#000000] text-white rounded-tl rounded-tr border p-4 gap-2">
                      <h3 class="text-xl sm:text-2xl text-center">Contact Preview</h3>
                      <p class="text-xs sm:text-sm text-center opacity-80">For demonstration purposes only</p>
                    </div>
                    <div class="live__preview__content px-5 flex flex-col gap-6 pt-6">
                      <div class="flex justify-center profile">
                        <div class="w-32 h-32 justify-center flex items-center rounded-full bg-[#00000020] bg-cover bg-no-repeat border bg-center" alt="" :style="{backgroundImage: `url(${form.logo})`}">
                          <span v-if="!form.logo" class="text-center text-4xl font-bold">{{ form.first_name.charAt(0) + form.last_name.charAt(0) }}</span>
                        </div>
                      </div>
                      <div class="name_info flex flex-col justify-center gap-1">
                        <div class="name text-center font-bold text-xl">{{ form.first_name }} {{ form.last_name }}</div>
                        <!--                              <div class="company text-center"> {{ form.company }} </div>-->
                      </div>
                      <div class="entry flex gap-4">
                        <Icon name="phone" class="w-5 h-5 " />
                        <div class="flex gap-2 flex-col">
                          <div v-for="(phone, pk) in form.phones" :key="pk" class="box flex flex-col bg-white">
                            <h5 class="text-md font-bold">{{ phone.type }}</h5>
                            <span class="value">{{ phone.phone }}</span>
                          </div>
                        </div>
                      </div>
                      <div class="entry flex gap-4">
                        <Icon name="email" class="w-5 h-5 " />
                        <div class="flex gap-2 flex-col">
                          <div v-for="(email, ek) in form.emails" :key="ek" class="box flex flex-col bg-white">
                            <h5 class="text-md font-bold">{{ email.type }}</h5>
                            <span class="value">{{ email.email }}</span>
                          </div>
                        </div>
                      </div>
                      <div class="entry flex gap-4">
                        <Icon name="website" class="w-5 h-5 " />
                        <div class="flex gap-2 flex-col">
                          <div v-for="(website, wk) in form.websites" :key="wk" class="box flex flex-col bg-white">
                            <h5 class="text-md font-bold">{{ website.type }}</h5>
                            <span class="value">{{ website.website }}</span>
                          </div>
                        </div>
                      </div>
                      <div class="entry flex gap-4">
                        <Icon name="address" class="w-5 h-5 " />
                        <div class="flex gap-2 flex-col">
                          <div v-for="(address, ak) in form.addresses" :key="ak" class="box flex flex-col bg-white">
                            <h5 class="text-md font-bold">{{ address.type }}</h5>
                            <span class="font-semibold">{{ address.name }}</span>
                            <span class="value">{{ address.address }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
    </div>
    <!-- Loading Process -->
    <div v-if="form.processing" class="processing-overlay">
      <div class="background" />
      <div class="loader">
        <svg width="50px" height="50px" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid" style="background: none;">
          <circle cx="75" cy="50" fill="#ffffff" r="6.39718">
            <animate attributeName="r" values="4.8;4.8;8;4.8;4.8" times="0;0.1;0.2;0.3;1" dur="1s" repeatCount="indefinite" begin="-0.875s" />
          </circle>
          <circle cx="67.678" cy="67.678" fill="#ffffff" r="4.8">
            <animate attributeName="r" values="4.8;4.8;8;4.8;4.8" times="0;0.1;0.2;0.3;1" dur="1s" repeatCount="indefinite" begin="-0.75s" />
          </circle>
          <circle cx="50" cy="75" fill="#ffffff" r="4.8">
            <animate attributeName="r" values="4.8;4.8;8;4.8;4.8" times="0;0.1;0.2;0.3;1" dur="1s" repeatCount="indefinite" begin="-0.625s" />
          </circle>
          <circle cx="32.322" cy="67.678" fill="#ffffff" r="4.8">
            <animate attributeName="r" values="4.8;4.8;8;4.8;4.8" times="0;0.1;0.2;0.3;1" dur="1s" repeatCount="indefinite" begin="-0.5s" />
          </circle>
          <circle cx="25" cy="50" fill="#ffffff" r="4.8">
            <animate attributeName="r" values="4.8;4.8;8;4.8;4.8" times="0;0.1;0.2;0.3;1" dur="1s" repeatCount="indefinite" begin="-0.375s" />
          </circle>
          <circle cx="32.322" cy="32.322" fill="#ffffff" r="4.80282">
            <animate attributeName="r" values="4.8;4.8;8;4.8;4.8" times="0;0.1;0.2;0.3;1" dur="1s" repeatCount="indefinite" begin="-0.25s" />
          </circle>
          <circle cx="50" cy="25" fill="#ffffff" r="6.40282">
            <animate attributeName="r" values="4.8;4.8;8;4.8;4.8" times="0;0.1;0.2;0.3;1" dur="1s" repeatCount="indefinite" begin="-0.125s" />
          </circle>
          <circle cx="67.678" cy="32.322" fill="#ffffff" r="7.99718">
            <animate attributeName="r" values="4.8;4.8;8;4.8;4.8" times="0;0.1;0.2;0.3;1" dur="1s" repeatCount="indefinite" begin="0s" />
          </circle>
        </svg>
      </div>
    </div>
    <!-- Loading Process -->
  </SidebarNavigation>
</template>

<script>
import Layout from '@/Shared/ToolLayout'
import TextInput from '@/Shared/TextInput'
import Icon from '@/Shared/Icon'
import {toPng } from 'html-to-image'
import TextareaInput from '@/Shared/TextareaInput.vue'
import SidebarNavigation from '@/Shared/SidebarNavigation.vue'
import { validateEmail, validateURL, validatePhone, validateFileSize, validateFileType } from '../../utils/validation.js'

export default {
    components: {
        SidebarNavigation,
        TextareaInput,
        Icon,
        TextInput,
    },
    layout: Layout,
    props: {
        title: String,
        html: String,
        uri: {
            type: String,
            required: false,
        },
    },
    remember: 'form',
    data() {
        return {
            html_code: this.html,
            colors: [{name: 'black', code: '#000000'}, {name: 'green', code: '#06752d'}, {name: 'red', code: '#af191c'}, {name: 'blue', code: '#015b90'}],
            copy_text: '',
            processing_code: '',
            copy_text_html: '',
            vcf_file_url: 'https://save-to-contacts.com/card/id/demo.vcf',
            referral_link_url: 'https://refer.direct/demo',
            selected_color: 0,
            code: 'const foo = \'bar\';',
            step: 3,
            file_accept: '.csv,.xlsx,.tsv,.ods,.xls,.slk,.xml',
            validationErrors: {},
            form: this.$inertia.form({
                first_name: '',
                last_name: '',
                phones: [],
                emails: [],
                websites: [],
                addresses: [],
                logo: null,
                card_link: '',
                file_type: null,
            }),
            enable_csv_import: false,
            cardPages: [],
            print_preview: false,
            allPortraitCardDesigns: [0,1,2,3,4,5,6,7,8,9,10,11,12,13,14],
            allLandscapeCardDesigns: [0,1,2,3,4,5,6,7,8,9,10,11,12,13,14,15],
            cardPortraitDesigns: [],
            cardLandscapeDesigns: [],
            page_number_portrait: 1,
            page_number_landscape: 1,
            selected_design: { number: 1, type: 'portrait' },
            current_card: null,
            paper: {
                size: 'a4',
            },
            card_layout: 'horizontal',
            referral_layout: 'horizontal',
            refreshed_user_at: (new Date()).valueOf(),
        }
    },
    created() {
        this.setForm({
            first_name: 'John',
            last_name: 'Doe',
            phones: [{'type': 'Work', 'phone': '833-000-0000'}],
            emails: [{'type': 'Work', 'email': 'my-email@gmail.com'}],
            websites: [{'type': 'Work', 'website': 'example.com'}],
            addresses: [{'type': 'Work', 'name': 'ABC Motors', 'address': '3611 N Main St, Houston, TX 77009' }],
            logo: '',
        })
    },
    methods: {
        validatePhoneEntry(phone, index) {
            if (phone && phone.phone) {
                const result = validatePhone(phone.phone)
                if (!result.valid) {
                    this.validationErrors[`phones.${index}`] = result.message
                } else {
                    delete this.validationErrors[`phones.${index}`]
                }
            } else {
                delete this.validationErrors[`phones.${index}`]
            }
        },
        validateEmailEntry(email, index) {
            if (email && email.email) {
                const result = validateEmail(email.email)
                if (!result.valid) {
                    this.validationErrors[`emails.${index}`] = result.message
                } else {
                    delete this.validationErrors[`emails.${index}`]
                }
            } else {
                delete this.validationErrors[`emails.${index}`]
            }
        },
        validateWebsiteEntry(website, index) {
            if (website && website.website) {
                const result = validateURL(website.website)
                if (!result.valid) {
                    this.validationErrors[`websites.${index}`] = result.message
                } else {
                    delete this.validationErrors[`websites.${index}`]
                    // Normalize URL
                    if (result.normalized) {
                        website.website = result.normalized
                    }
                }
            } else {
                delete this.validationErrors[`websites.${index}`]
            }
        },
        validateFileUpload(file, type = 'image') {
            if (!file) return { valid: true }

            const sizeResult = validateFileSize(file, 5)
            if (!sizeResult.valid) {
                if (window.$toast) {
                    window.$toast.error(sizeResult.message)
                }
                return sizeResult
            }

            if (type === 'image') {
                const typeResult = validateFileType(file)
                if (!typeResult.valid) {
                    if (window.$toast) {
                        window.$toast.error(typeResult.message)
                    }
                    return typeResult
                }
            }

            return { valid: true }
        },
        generateVCF(){
            this.axios.post('/card/generateVCF',  this.form).then((response) => {
                if( response.data && response.data.card_link){
                    this.form.card_link = response.data.card_link
                    if (window.$toast) {
                        window.$toast.success('vCard link generated successfully!')
                    }
                }
            }).catch((error) => {
                console.log(error)
                this.form.processing = false
                if (window.$toast) {
                    window.$toast.error('Failed to generate vCard link. Please try again.')
                }
            })
        },
        async copyURL() {
            try {
                await navigator.clipboard.writeText(this.form.card_link)
                this.copy_text = 'The VCF card link has been Copied!'
                if (window.$toast) {
                    window.$toast.success('URL copied to clipboard!')
                }
                this.hideCopy()
            } catch($e) {
                this.copy_text = 'Cannot copy the link!'
                if (window.$toast) {
                    window.$toast.error('Failed to copy URL. Please try again.')
                }
                this.hideCopy()
            }
        },
        async copyCode() {
            try {
                await navigator.clipboard.writeText(this.html_code)
                this.copy_text_html = 'The code has been Copied!'
                if (window.$toast) {
                    window.$toast.success('Code copied to clipboard!')
                }
                this.hideCopy()
            } catch($e) {
                this.copy_text_html = 'Cannot copy the code!'
                if (window.$toast) {
                    window.$toast.error('Failed to copy code. Please try again.')
                }
                this.hideCopy()
            }
        },
        hideCopy(){
            const that = this
            setTimeout(function(){that.copy_text = ''; that.copy_text_html = ''}, 3000)
        },
        removeEntry(key, arr){
            arr.splice(key, 1)
        },
        addNewEntry(type){
            const entry_keys = {'email': 'emails', 'phone': 'phones', 'website': 'websites', 'address': 'addresses'}
            const empty_data = {
                'address' : {'type': 'Work', 'name': '', 'address': '', 'address_2': '', 'city': '', 'region': '', 'country': '', 'zip': '' },
                'phone' : {'type': 'Home', 'phone': ''},
                'email' : {'type': 'Work', 'email': ''},
                'website' : {'type': 'Work', 'website': ''},
            }
            this.form[entry_keys[type]].push(empty_data[type])
        },
        changeTextAlign(e){
            if(this.card_layout === 'horizontal'){
                const length = e.currentTarget.value.length
                if(length > 14){
                    this.$refs.user__name.style.textAlign = 'center'
                    this.$refs.user__name.style.width = '120px'
                }else{
                    this.$refs.user__name.style.textAlign = 'left'
                    this.$refs.user__name.style.width = 'auto'
                }
                this.$refs.user__email.style.paddingLeft = this.$refs.user__name.offsetLeft ? this.$refs.user__name.offsetLeft + 'px' : '29px'
            }
        },
        changeFontSize(e){
            if(this.card_layout === 'vertical'){
                const length = e.currentTarget.value.length
                // this.$refs.email_input.style.fontSize = length > 27 ? 27/length * 12 + 'px' : '12px'
            }
        },
        downloadAsPNG(referral){
            const target_selector = document.getElementsByClassName(referral ? 'card__content__referral' : 'card__content')
            const target = target_selector?target_selector[0]: null
            const gen_id = Math.floor(Date.now() /1000)
            toPng(target, {pixelRatio: 6}).then((dataUrl) => {
                const link = document.createElement('a')
                link.download = gen_id + '.' + 'png'
                link.href = dataUrl
                link.click()
            })
                .catch((err) => {
                    console.log(err)
                })
        },
        setForm(elements){
            const that = this
            Object.keys(elements).forEach(function (element) {
                that.form[element] = elements[element]
            })
        },
        partitionArray(array, size){
            return array.map( (e,i) => (i % size === 0) ? array.slice(i, i + size) : null ).filter( (e) => e )
        },
        withPhotosCheck(){
            if(this.form.with_photos){
                this.file_accept = '.zip'
            }
        },
        logoInputChange(e){
            if(e.target.files.length && e.target.files[0]){
                const file = e.target.files[0]
                this.form.file_type = file.type
                const validation = this.validateFileUpload(file, 'image')
                if (!validation.valid) {
                    e.target.value = ''
                    return
                }

                const self = this
                const reader = new FileReader()
                reader.readAsDataURL(file)
                reader.addEventListener('load', function(){
                    self.form.logo = reader.result
                    if (window.$toast) {
                        window.$toast.success('Photo uploaded successfully')
                    }
                })
            }
        },
        enableCsvImport(boolean){
            let el = document.getElementById('cardInputs'), all = el.getElementsByTagName('input'), i
            for (i = 0; i < all.length; i++) {
                if(all[i].type.toLowerCase() === 'text'){
                    all[i].disabled = boolean
                    if(boolean){
                        all[i].parentElement.classList.add('opacity-30')
                    }else{
                        all[i].parentElement.classList.remove('opacity-30')
                    }
                }
            }
        },
        fileInputChange(e) {
            this.form.processing = true
            let data = new FormData()
            const fileSel = document.getElementById('file')
            if(fileSel.files.length && fileSel.files[0]){
                data.append('file', document.getElementById('file').files[0])
                this.axios.post(this.route('import.file'), data).then(( response ) => {
                    if(response.data && response.data.csv && response.data.csv.length){
                        this.setCard(response.data.csv)
                        this.generateCardDesign()
                        this.form.processing = false
                        this.step = 2
                    }else{
                        if (window.$toast) {
                            window.$toast.error('Something went wrong while importing the file. Please try again.')
                        }
                        this.form.processing = false
                    }
                }).catch((error) => {
                    this.form.processing = false
                    console.error('File upload error:', error)

                    let errorMessage = 'Failed to import file. Please try again.'
                    if (error.response) {
                        if (error.response.status === 413) {
                            errorMessage = 'File is too large. Please use a smaller file.'
                        } else if (error.response.status === 415) {
                            errorMessage = 'Unsupported file type. Please use CSV, XLSX, or TSV format.'
                        } else if (error.response.data && error.response.data.message) {
                            errorMessage = error.response.data.message
                        }
                    } else if (error.message) {
                        if (error.message.includes('Network')) {
                            errorMessage = 'Network error. Please check your connection and try again.'
                        }
                    }

                    if (window.$toast) {
                        window.$toast.error(errorMessage)
                    }
                })
            }
        },
        setCard(data){
            this.form.cards = [...data]
            const cardData = this.form.cards.map((c, ci) => {
                const qrText = c.name + ' \n' + c.profession + ' \n' + c.location + ' \n' + c.phone + ' \n' + c.email + ' \n' + c.website
                return {name: qrText, index: ci}
            })
            this.createQr(cardData)
        },
        createQr(cardData){
            this.axios.post(this.route('create.qr'), { data: cardData }).then((response) => {
                if( response.data && response.data.images && response.data.images.length ){
                    this.setQrImageToCards(response.data.images)
                }
            }).catch((error) => {
                console.log(error)
                this.form.processing = false
            })
        },
        fileBrowse() {
            if(this.form.school_name){
                this.$refs.file.click()
            }else{
                if (window.$toast) {
                    window.$toast.warning('Please input school name first!')
                }
            }
        },
    },
}
</script>





